import { Link } from "@frontend/lib/link";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { formatDateTime } from "@creators/backend/lib/time";
import { clientApi } from "@frontend/lib/api";
import { useAsync } from "@frontend/lib/use-async";
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

type ContractRow = {
  terms: { acceptedBy: string; acceptedAt: string | Date };
  collab: { id: string };
  html: string;
};

export function ContractPage() {
  const { id = "" } = useParams<{ id: string }>();
  useEffect(() => { document.title = "Agreement"; }, []);

  const state = useAsync(
    async () => {
      if (!/^[0-9a-f-]{36}$/i.test(id)) return null;
      return clientApi<ContractRow>(`/api/collabs/${id}/contract`).catch(() => null);
    },
    [id],
    { cacheKey: /^[0-9a-f-]{36}$/i.test(id) ? `contract:${id}` : null },
  );

  if (state.status === "loading" && !state.data) {
    return <PageSkeleton />;
  }
  if (!state.data) return <Navigate to={`/collabs/${id}`} replace />;
  const row = state.data;
  const acceptedBy = row.terms.acceptedBy === "brand" ? "Collaborator" : "Creator";

  return (
    <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 py-10">
      <Link href={`/collabs/${id}`} className="text-sm text-ink-muted hover:text-ink">
        ← Back to the collab
      </Link>

      <article
        className="mt-5 border border-line bg-surface px-7 py-9 leading-relaxed text-ink shadow-sm shadow-ink/[0.03]
          [&_header]:mb-6 [&_header]:border-b [&_header]:border-line [&_header]:pb-4
          [&_h1]:text-[1.35rem] [&_h1]:font-semibold [&_h1]:tracking-tight
          [&_.meta]:mt-1.5 [&_.meta]:text-[13px] [&_.meta]:text-ink-muted
          [&_h2]:mt-7 [&_h2]:text-[11px] [&_h2]:font-semibold [&_h2]:tracking-[0.08em] [&_h2]:text-ink-muted [&_h2]:uppercase
          [&_p]:mt-2.5 [&_p]:text-[15px] [&_p]:leading-[1.65]
          [&_table]:mt-4 [&_table]:w-full [&_table]:text-sm
          [&_th]:border-b [&_th]:border-line [&_th]:pb-2 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-semibold [&_th]:tracking-[0.06em] [&_th]:text-ink-muted [&_th]:uppercase
          [&_td]:border-b [&_td]:border-line/80 [&_td]:py-2.5"
        dangerouslySetInnerHTML={{ __html: row.html }}
      />

      <p className="mt-6 text-xs text-ink-subtle">
        Accepted by the {acceptedBy} on {formatDateTime(row.terms.acceptedAt)}.
      </p>
    </main>
  );
}
